import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { estApiError } from './errors.js';

/**
 * Demande de création de site — `/v1/g/:slug/demande-site`. Le site est une
 * prestation à part, sur devis : la galerie décrit son projet, l'équipe
 * Artosera la suit dans la console. Les formes suivent le contrat de l'API au
 * champ près (même règle que `api/site.ts`).
 */

export type EtatDemandeSite = 'envoyee' | 'en_cours' | 'traitee' | 'abandonnee';
export type DelaiDemandeSite = 'des_que_possible' | 'trois_mois' | 'six_mois' | 'sans_contrainte';
export type MoyenContactSite = 'email' | 'telephone' | 'visio';
export type LangueDemandeSite = 'fr' | 'en';

export const DELAIS_DEMANDE_SITE: readonly DelaiDemandeSite[] = [
  'des_que_possible',
  'trois_mois',
  'six_mois',
  'sans_contrainte',
];
export const MOYENS_CONTACT_SITE: readonly MoyenContactSite[] = ['email', 'telephone', 'visio'];

export interface ContactDemandeSite {
  readonly nom: string;
  readonly email: string;
  readonly telephone: string | null;
  readonly moyen: MoyenContactSite;
}

export interface DemandeSiteVue {
  readonly id: string;
  readonly etat: EtatDemandeSite;
  readonly siteExistant: boolean;
  readonly urlActuelle: string | null;
  readonly objectifs: string;
  readonly langues: readonly LangueDemandeSite[];
  readonly visiteVirtuelle: boolean;
  readonly delai: DelaiDemandeSite;
  readonly budget: string | null;
  readonly message: string;
  readonly contact: ContactDemandeSite;
  readonly envoyeeLe: string;
  readonly traiteeLe: string | null;
}

export interface ReponseDemandesSite {
  readonly demandes: readonly DemandeSiteVue[];
  readonly enCours: DemandeSiteVue | null;
}

export interface CorpsDemandeSite {
  readonly siteExistant: boolean;
  readonly urlActuelle: string | null;
  readonly objectifs: string;
  readonly langues: readonly LangueDemandeSite[];
  readonly visiteVirtuelle: boolean;
  readonly delai: DelaiDemandeSite;
  readonly budget: string | null;
  readonly message?: string;
  readonly contact: {
    readonly nom: string;
    readonly email: string;
    readonly telephone: string | null;
    readonly moyen: MoyenContactSite;
  };
}

const chemin = (galerie: string) => `/g/${encodeURIComponent(galerie)}/demande-site`;

export const demandeSiteApi = {
  lister: (galerie: string, appel?: AppelApi) =>
    api.get<ReponseDemandesSite>(chemin(galerie), { galerie, ...appel }),
  envoyer: (galerie: string, corps: CorpsDemandeSite, appel?: AppelApi) =>
    api.post<{ demande: DemandeSiteVue }>(chemin(galerie), corps, { galerie, ...appel }),
};

const cleDemandesSite = (galerie: string) => cleMetier(galerie, 'demande-site');

export function useDemandesSite(galerie: string) {
  return useQuery({
    queryKey: cleDemandesSite(galerie),
    queryFn: (contexte) => demandeSiteApi.lister(galerie, appelDeRequete(contexte)),
  });
}

/**
 * Envoie la demande. En cas de succès la demande ouverte entre dans le cache
 * (l'encart « en cours » remplace le formulaire sans attendre) ; un `409` dit
 * qu'une demande est déjà ouverte : on relit pour la montrer.
 */
export function useEnvoyerDemandeSite(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsDemandeSite, appel) => demandeSiteApi.envoyer(galerie, corps, appel),
      onSuccess: ({ demande }) => {
        client.setQueryData(
          cleDemandesSite(galerie),
          (precedent: ReponseDemandesSite | undefined): ReponseDemandesSite => ({
            demandes: [demande, ...(precedent?.demandes ?? [])].slice(0, 20),
            enCours: demande,
          }),
        );
      },
      onError: (erreur) => {
        if (estApiError(erreur) && erreur.status === 409) {
          void client.invalidateQueries({ queryKey: cleDemandesSite(galerie) });
        }
      },
    }),
  );
}

/** La raison précise d'une erreur d'API (`details.raison`), si l'API en donne une. */
export function raisonErreur(erreur: unknown): string | null {
  if (!estApiError(erreur)) return null;
  const details = erreur.details;
  if (typeof details !== 'object' || details === null) return null;
  const { raison } = details as { raison?: unknown };
  return typeof raison === 'string' ? raison : null;
}
